import { memoize } from 'lodash-es';

// Default statuses if the environment variable is not set or invalid
const DEFAULT_STATUSES = [
  'Urgent',
  'Unread',
  'In Progress',
  'Read',
  'Resolved',
  'Client Contacted',
  'Not Applicable',
];

interface AlertStatusOption {
  value: string; // lowercase version for internal use/keys
  label: string; // Original casing for display
}

/**
 * Parses the VITE_ALERT_STATUS_OPTIONS environment variable.
 * Returns an array of alert status options with value (lowercase) and label (original).
 * Uses defaults if the environment variable is not set.
 * Memoized to avoid re-parsing on every call.
 */
export const getAlertStatusOptions = memoize((): AlertStatusOption[] => {
  const envStatuses = import.meta.env.VITE_ALERT_STATUS_OPTIONS;

  let statusLabels: string[] = DEFAULT_STATUSES;

  if (typeof envStatuses === 'string' && envStatuses.trim() !== '') {
    const parsedStatuses = envStatuses.split(',').map(s => s.trim()).filter(Boolean);
    if (parsedStatuses.length > 0) {
      statusLabels = parsedStatuses;
    }
  }

  return statusLabels.map(label => ({
    value: label.toLowerCase(),
    label: label,
  }));
});

/**
 * Helper to get just the lowercase status values.
 */
export const getAlertStatusValues = memoize((): string[] => {
    return getAlertStatusOptions().map(option => option.value);
});

/**
 * Helper to get just the status labels.
 */
export const getAlertStatusLabels = memoize((): string[] => {
    return getAlertStatusOptions().map(option => option.label);
});

/**
 * Gets the display label for a given status value (case-insensitive).
 * @param statusValue The lowercase status value.
 * @returns The display label or the original value if not found.
 */
export const getStatusLabel = (statusValue: string): string => {
    const lowerValue = statusValue?.toLowerCase();
    const option = getAlertStatusOptions().find(opt => opt.value === lowerValue);
    return option?.label || statusValue; // Return original value as fallback
}; 